import { create } from 'zustand';
import profileService from '../api/services/profile.service';
import useMyProfileStore from './useMyProfileStore';

/* The Profile page reads the signed-in parent out of the live /myProfile record
   that AppLayout already fetched, and writes edits back through one PATCH.

   That PATCH merges into user_metadata rather than replacing it, but it takes
   the whole parent block at once — so every save re-sends the current value of
   everything it isn't changing. */

/* childData birthDates arrive as full ISO timestamps; the date helpers on this
   page all work in yyyy-mm-dd. */
export const dobOf = (kid) => String(kid?.birthDate || '').slice(0, 10);

/* Tags are stored with their emoji ("🏈 Football"), so matching a stored tag
   against an activity label means dropping everything before the first letter
   or digit. */
export const tagLabel = (tag) => String(tag || '').replace(/^[^\p{L}\p{N}]+/u, '').trim();

/* user_metadata → the shape this page renders. */
export const readProfile = (me) => {
  const m = me?.user_metadata || {};
  return {
    firstName: m.firstName || '',
    lastName: m.lastName || '',
    name: [m.firstName, m.lastName].filter(Boolean).join(' ') || m.name || '',
    photo: m.profilePic || '',
    zip: m.zipCode || '',
    role: m.Iam || '',
    location: m.location || null,
    bio: m.about || '',
    kids: Array.isArray(m.childData) ? m.childData : [],
    interests: Array.isArray(m.tags) ? m.tags : [],
    /* [{ _id, name, photo, classes: [{ _id, name, parentClassId }] }] */
    schools: Array.isArray(m.schoolClassDetails) ? m.schoolClassDetails : [],
    spouseInvite: me?.spouseInvite || null,
    invitedBy: me?.invitedBy || null,
  };
};

const useProfileStore = create((set, get) => ({
  saving: false,
  interestOptions: [],
  interestsLoading: false,

  loadInterestOptions: async () => {
    if (get().interestOptions.length || get().interestsLoading) return;
    set({ interestsLoading: true });
    try {
      set({ interestOptions: await profileService.activities() });
    } finally {
      set({ interestsLoading: false });
    }
  },

  /* `patch` carries only what changed; the rest comes off the record on screen.
     The saved record is re-read afterwards so the page shows what the server
     actually kept. */
  save: async (patch) => {
    const me = useMyProfileStore.getState().me;
    const current = readProfile(me);
    set({ saving: true });
    try {
      await profileService.updateParentProfile({
        firstName: patch.firstName ?? current.firstName,
        lastName: patch.lastName ?? current.lastName,
        about: patch.bio ?? current.bio,
        tags: patch.interests ?? current.interests,
        kids: patch.kids ?? current.kids,
        zipCode: patch.zip ?? current.zip,
        role: patch.role ?? current.role,
        location: patch.location ?? current.location,
        profilePic: patch.photoFile || null,
      });
      await useMyProfileStore.getState().fetchMyProfile();
    } finally {
      set({ saving: false });
    }
  },
}));

export default useProfileStore;
